import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle, X, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/hooks/useAuth";
import { authClient } from "@/auth/client";

const FLAG = "psihotron:therapist-approved";

interface AccountStatus {
  role: string;
  therapistStatus: string | null;
}

// Watches for the moment an admin approves a logged-in therapist. better-auth
// caches the session (with the old `client` role) in a signed cookie for 5
// minutes, so the header's dashboard button would otherwise stay hidden until
// the therapist logs out and back in. We poll the DB-backed account-status
// endpoint, and on the pending→approved transition we (1) force-refresh the
// session past the cookie cache so the dashboard button appears, and (2) raise
// a persistent banner so the therapist understands what just happened.
export function ApprovalBanner() {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  // Only a not-yet-promoted account can be waiting on approval.
  const roleIsClient = !user?.role || user.role === "client" || user.role === "user";

  // Restore a not-yet-dismissed banner across reloads/navigation (client-only,
  // avoids SSR localStorage access + hydration mismatch).
  useEffect(() => {
    if (localStorage.getItem(FLAG) === "1") setShow(true);
  }, []);

  const { data } = useQuery<AccountStatus | null>({
    queryKey: ["me", "account-status"],
    queryFn: () =>
      fetch("/api/me/account-status").then((r) => (r.ok ? r.json() : null)),
    enabled: isAuthenticated && roleIsClient,
    // One fetch for plain clients; poll only while an application is pending.
    refetchInterval: (q) =>
      q.state.data?.therapistStatus === "pending" ? 30_000 : false,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  useEffect(() => {
    if (!roleIsClient) return;
    if (data?.role !== "therapist" || data?.therapistStatus !== "approved") return;

    // Approval happened mid-session. Pull a fresh session bypassing the cookie
    // cache (the client maps `disableCookieCache` → the `l` query param), then
    // prime the query so the header re-renders with the dashboard button.
    (async () => {
      const { data: fresh } = await authClient.getSession({
        query: { disableCookieCache: true },
      });
      if (fresh) queryClient.setQueryData(["auth", "session"], fresh);
      queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
      localStorage.setItem(FLAG, "1");
      setShow(true);
    })();
  }, [data?.role, data?.therapistStatus, roleIsClient, queryClient]);

  function dismiss() {
    localStorage.removeItem(FLAG);
    setShow(false);
  }

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-0 top-[3.5rem] z-40 flex items-center justify-center gap-3 px-4 py-2.5 text-center text-[0.9rem] font-medium text-white sm:px-6"
      style={{
        background:
          "linear-gradient(135deg, oklch(37.12% 0.059 186.62), oklch(29.52% 0.0501 186.62))",
        boxShadow: "0 4px 20px oklch(37.12% 0.059 186.62 / 0.3)",
      }}
      role="status"
    >
      <CheckCircle weight="fill" className="size-5 shrink-0" />
      <span className="flex-1 sm:flex-none">{t("approvalBanner.message")}</span>
      <Link
        to="/dashboard"
        onClick={dismiss}
        className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3.5 py-1.5 text-[0.825rem] font-semibold transition-colors hover:bg-white/25"
      >
        {t("approvalBanner.cta")}
        <ArrowRight className="size-3.5" />
      </Link>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t("approvalBanner.dismiss")}
        className="absolute right-3 inline-flex size-7 items-center justify-center rounded-full transition-colors hover:bg-white/20 sm:static"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}
